import React, { ChangeEvent, FC } from "react";
import Input from "components/Input/Input";
import { useCurrencySymbol } from "hooks/useCurrencySymbol";
import { useTranslation } from "react-i18next";
import formatter from "../../../../utils/formatter";
import {
  AddFundsAmountRow,
  AddFundsAmountValue,
  AddFundsContainer,
  AddFundsFormCol,
  AddFundsFormRow,
} from "../../styles/BooksAddFunds.style";

interface BooksAddFundsAmountFieldsProps {
  amount: string;
  amountError?: string;
  touchedAmount?: boolean;
  onAmountChange: (event: ChangeEvent<HTMLInputElement>) => void;
  feePercent: string;
  inputHeight?: number;
}

const BooksAddFundsAmountFields: FC<BooksAddFundsAmountFieldsProps> = React.memo(
  ({
    amount,
    amountError,
    touchedAmount,
    onAmountChange,
    feePercent,
    inputHeight = 34,
  }) => {
    const { t } = useTranslation("common");
    const currencySymbol = useCurrencySymbol();
    const amountValue = parseFloat(amount);
    const amountFloat = Number.isFinite(amountValue) ? amountValue : 0;
    const parsedFeePercent = parseFloat(feePercent);
    const feeRate = Number.isFinite(parsedFeePercent) ? parsedFeePercent : 0;
    const commissionFeeValue = formatter.roundTo2Decimal(feeRate * amountFloat);
    const totalAmount = formatter.roundTo2Decimal(amountFloat + commissionFeeValue);
    const commissionFeePercentLabel = Math.round(feeRate * 100);
    const hasEnteredAmount = amount.trim() !== "";

    const formatAmountValue = (value: number) =>
      `${currencySymbol} ${formatter.currency(value)}`;
    const amountPlaceholder = `${currencySymbol} 0.00`;

    return (
      <AddFundsContainer fluid>
        <AddFundsFormRow>
          <AddFundsFormCol xs={12}>
            <AddFundsAmountRow>
              {t("books.addFundsPanel.addAmountToBalance")}
              <AddFundsAmountValue>
                <Input
                  name="amount"
                  value={amount}
                  onChange={onAmountChange}
                  invalid={!!touchedAmount && !!amountError}
                  placeholder={amountPlaceholder}
                  tooltip={amountError}
                  type="number"
                  height={inputHeight}
                />
              </AddFundsAmountValue>
            </AddFundsAmountRow>
          </AddFundsFormCol>
        </AddFundsFormRow>
        <AddFundsFormRow>
          <AddFundsFormCol xs={12}>
            <AddFundsAmountRow>
              {t("books.addFundsPanel.paymentProcessingFee", {
                feePercent: commissionFeePercentLabel,
              })}
              <AddFundsAmountValue>
                <Input
                  value={hasEnteredAmount ? formatAmountValue(commissionFeeValue) : ""}
                  placeholder={amountPlaceholder}
                  readOnly
                  tabIndex={-1}
                  height={inputHeight}
                />
              </AddFundsAmountValue>
            </AddFundsAmountRow>
          </AddFundsFormCol>
        </AddFundsFormRow>
        <AddFundsFormRow>
          <AddFundsFormCol xs={12}>
            <AddFundsAmountRow>
              {t("shipping.total")}
              <AddFundsAmountValue>
                <Input
                  value={hasEnteredAmount ? formatAmountValue(totalAmount) : ""}
                  placeholder={amountPlaceholder}
                  readOnly
                  tabIndex={-1}
                  height={inputHeight}
                />
              </AddFundsAmountValue>
            </AddFundsAmountRow>
          </AddFundsFormCol>
        </AddFundsFormRow>
      </AddFundsContainer>
    );
  },
);

export default BooksAddFundsAmountFields;
